export const PAY_EVENT_FACT_MAKER_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FINANCE_OFFICER',
  'FACILITY_MANAGER',
  'PAYROLL_OFFICER',
  'COORDINATOR',
  'HR_MANAGER',
  'CLINICAL_LEAD',
  'ENROLLED_NURSE',
  'REGISTERED_NURSE',
] as const;

export const PAY_EVENT_FACT_CHECKER_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FINANCE_OFFICER',
  'FACILITY_MANAGER',
  'PAYROLL_OFFICER',
  'HR_MANAGER',
  // Coordinators do the rostering that mints these facts. Leaving them out meant
  // the person who captured the fact could never clear it.
  'COORDINATOR',
] as const;

/**
 * The pay-fact review queue lives at
 * `/settings/payroll/eba/<ebaId>/manual-facts`. Until 9 Sep 2026 the whole
 * Settings > Payroll > EBA area was limited to Tenant and System Admins, so a
 * payroll officer who corrected a time (which re-submits the row's approved pay
 * facts) could not open the page holding Approve / Supersede and re-submit.
 * The leaf alone now admits the checker cohort; the rest of the EBA workflow
 * stays admin-only. Keep this pattern and `PAY_EVENT_FACT_QUEUE_ROLES` in
 * lockstep with `settings-access.ts` and `route-access.ts`.
 */
export const PAY_EVENT_FACT_QUEUE_PATH_PATTERN =
  /^\/settings\/payroll\/eba\/[^/]+\/manual-facts(?:\/|$)/;

export function isPayEventFactQueuePath(pathname: string): boolean {
  return PAY_EVENT_FACT_QUEUE_PATH_PATTERN.test(pathname);
}

export function payEventFactQueueHref(ebaId: string): string {
  return `/settings/payroll/eba/${encodeURIComponent(ebaId)}/manual-facts`;
}

/** Who may open the pay-fact review queue page: the checker cohort. */
export const PAY_EVENT_FACT_QUEUE_ROLES = PAY_EVENT_FACT_CHECKER_ROLES;

export interface PayEventFactIntegrityRow {
  status?: string | null;
  integrity_status?: string | null;
  missing_required_facts?: unknown[] | null;
  source_digest_sha256?: string | null;
  evidence_digest_sha256?: string | null;
  submitted_by?: string | null;
  submitted_by_auth_uid?: string | null;
  approved_by?: string | null;
  approved_by_auth_uid?: string | null;
  locked_by?: string | null;
  locked_by_auth_uid?: string | null;
  /**
   * The `public.users` id of the worker this set pays, when the caller selected
   * it. Optional: this is a read-model precheck, and the canonical DB predicate
   * resolves the worker itself via `pay_event_fact_actor_is_the_worker`,
   * including across duplicate staff rows that share an email.
   */
  staff_user_id?: string | null;
}

const SHA256_HEX = /^[0-9a-f]{64}$/;

export function canPreparePayEventFacts(role: string | null | undefined): boolean {
  return Boolean(
    role && (PAY_EVENT_FACT_MAKER_ROLES as readonly string[]).includes(role.toUpperCase()),
  );
}

/**
 * Locking is a financial seal rather than BAU capture, so it keeps the narrower
 * role list that `lock_pay_event_fact_set_controlled` enforces. COORDINATOR can
 * review but not lock; without this the UI would show a Lock button that always
 * fails at the RPC.
 */
export const PAY_EVENT_FACT_LOCKER_ROLES = PAY_EVENT_FACT_CHECKER_ROLES.filter(
  (role) => role !== 'COORDINATOR',
);

export function canLockPayEventFacts(role: string | null | undefined): boolean {
  return Boolean(
    role && (PAY_EVENT_FACT_LOCKER_ROLES as readonly string[]).includes(role.toUpperCase()),
  );
}

export function canReviewPayEventFacts(role: string | null | undefined): boolean {
  return Boolean(
    role && (PAY_EVENT_FACT_CHECKER_ROLES as readonly string[]).includes(role.toUpperCase()),
  );
}

/**
 * Read-model precheck only. The canonical DB predicate additionally recomputes
 * both digests, and resolves the worker's identity itself, immediately before a
 * pay-run item can consume the fact set.
 *
 * This precheck is deliberately weaker than the database: `staff_user_id` is
 * optional, so where a caller has not selected it the self-pay test is left to
 * `pay_event_fact_set_is_payroll_ready`, which is enforced by
 * `guard_pay_run_item_pay_event_fact_integrity` on every pay-run item. Being
 * more permissive here is safe in that direction; do not invert it.
 */
export function hasCurrentPayEventFactApproval(
  row: PayEventFactIntegrityRow | null | undefined,
): boolean {
  if (!row) return false;
  const status = row.status?.trim().toUpperCase();
  if (row.integrity_status !== 'CURRENT' || !status || !['APPROVED', 'LOCKED'].includes(status)) {
    return false;
  }
  if ((row.missing_required_facts ?? []).length > 0) return false;
  if (!SHA256_HEX.test(row.source_digest_sha256 ?? '')) return false;
  if (!SHA256_HEX.test(row.evidence_digest_sha256 ?? '')) return false;
  if (
    !row.submitted_by ||
    !row.submitted_by_auth_uid ||
    !row.approved_by ||
    !row.approved_by_auth_uid
  ) {
    return false;
  }
  // Capturing a pay fact and approving it are the same operational act for a
  // coordinator, so they may be the same person. What must never happen is
  // someone signing off their own pay.
  if (row.staff_user_id && row.approved_by === row.staff_user_id) return false;
  // Locking is a deliberate financial seal rather than BAU capture, so it keeps
  // the distinct-locker requirement that lock_pay_event_fact_set_controlled
  // still enforces, and gains the self-pay rule on top.
  if (
    status === 'LOCKED' &&
    (!row.locked_by ||
      !row.locked_by_auth_uid ||
      row.locked_by === row.submitted_by ||
      row.locked_by_auth_uid === row.submitted_by_auth_uid ||
      (Boolean(row.staff_user_id) && row.locked_by === row.staff_user_id))
  ) {
    return false;
  }
  return true;
}
